import type { StoreLocatorLocation } from "@components/store-locator/controller";
import { activeSiteConfig } from "@config/site/site-config";
import { Logger, ServiceType, LogTag } from "@lib/monitoring/logger";

const { language } = activeSiteConfig;

interface GetPlaceDetailsResult {
    place: google.maps.places.Place;
    isFromCache: boolean;
}

// Locale cache for place details to avoid redundant API calls for the same place ID
const placeDetailsCache = new Map<string, google.maps.places.Place>();

export const getPlaceDetails = async (
    prediction: google.maps.places.AutocompleteSuggestion,
    location?: StoreLocatorLocation
): Promise<GetPlaceDetailsResult> => {
    if (!prediction?.placePrediction?.placeId) {
        throw new Error("Invalid prediction: Missing place ID");
    }
    const placeId = prediction.placePrediction.placeId;
    const cacheKey = `${placeId}-${language?.[0]}`;
    const logPayload = {
        tag: LogTag.STORES,
        place_id: placeId,
        language: language?.[0],
        location: location || "unknown",
    };
    if (placeDetailsCache.has(cacheKey)) {
        const result = placeDetailsCache.get(cacheKey);
        if (result) {
            Logger.info(ServiceType.GOOGLE, "Google Places details cache hit", {
                ...logPayload,
                isCached: true,
            });
            return Promise.resolve({ place: result, isFromCache: true });
        }
    }

    const place = new google.maps.places.Place({
        id: prediction.placePrediction.placeId,
        requestedLanguage: language?.[0],
    });

    // fetchFields populates this Place instance; do not depend on its return value.
    await place.fetchFields({
        // Fields that should be returned by the API. We don't need much here.
        fields: ["id", "addressComponents", "location", "viewport", "types"],
    });
    if (!place.location) {
        throw new Error("Google Places details response is missing location");
    }
    placeDetailsCache.set(cacheKey, place);

    Logger.info(ServiceType.GOOGLE, "Google Places details API call", {
        ...logPayload,
        isCached: false,
    });

    return { place, isFromCache: false };
};
